import { CheckCircle2, Circle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { PageCard } from './PageCard';
import type { KontentinoPage } from '../types';

interface PageSelectorProps {
  availablePages: KontentinoPage[];
  selectedPageIds: string[];
  onUpdateSelection: (pageIds: string[]) => void;
  onNext: () => void;
}

export function PageSelector({
  availablePages,
  selectedPageIds,
  onUpdateSelection,
  onNext,
}: PageSelectorProps) {
  const handleTogglePage = (pageId: string) => {
    if (selectedPageIds.includes(pageId)) {
      onUpdateSelection(selectedPageIds.filter((id) => id !== pageId));
    } else {
      onUpdateSelection([...selectedPageIds, pageId]);
    }
  };

  const handleSelectAll = () => {
    onUpdateSelection(availablePages.map((p) => p.id));
  };

  const handleDeselectAll = () => {
    onUpdateSelection([]);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Select Target Pages</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Choose which social media pages you want to schedule posts to
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleSelectAll}>
            Select All
          </Button>
          <Button variant="outline" size="sm" onClick={handleDeselectAll}>
            Clear
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {availablePages.map((page) => {
          const isSelected = selectedPageIds.includes(page.id);
          return (
            <div
              key={page.id}
              className={`border rounded-lg p-4 cursor-pointer transition-all ${
                isSelected
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-muted-foreground'
              }`}
              onClick={() => handleTogglePage(page.id)}
            >
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  {isSelected ? (
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                  ) : (
                    <Circle className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
                <PageCard page={page} />
              </div>
            </div>
          );
        })}
      </div>

      {availablePages.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          No pages available. Please connect your social media accounts in Kontentino.
        </div>
      )}

      <div className="flex justify-end pt-4">
        <Button
          onClick={onNext}
          disabled={selectedPageIds.length === 0}
        >
          Next: Assign Posts ({selectedPageIds.length}{' '}
          {selectedPageIds.length === 1 ? 'page' : 'pages'})
        </Button>
      </div>
    </div>
  );
}
